import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import TagsService, {
  type CreateTagScheduleInput,
  type GetTagSchedulesParams,
  type UpdateTagScheduleInput,
} from '~/services/TagsService';
import { formatUTCDateDateTimeWidget, isBetween } from '~/services/TimeService';

export function useGetTagSchedules(params?: GetTagSchedulesParams) {
  return useQuery({
    queryKey: ['tag-schedules', params?.tagId, params?.offset, params?.limit],
    queryFn: () => {
      if (!params) {
        throw new Error('params is required');
      }
      return TagsService.getTagSchedules(params);
    },
    enabled: !!params,
  });
}

export function useCreateTagSchedule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (data: CreateTagScheduleInput) => TagsService.createTagSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tag-schedules'] });
      enqueueSnackbar('Schedule created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating schedule: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateTagSchedule() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTagScheduleInput }) => TagsService.updateTagSchedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tag-schedules'] });
      enqueueSnackbar('Schedule updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating schedule: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useEndTag() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (tagId: string) => {
      const tag = await TagsService.getTag(tagId);
      const activeSchedules = (tag.schedules ?? []).filter((schedule) =>
        isBetween(schedule.activeStartDate, schedule.activeEndDate, undefined, undefined, '[]'),
      );

      if (activeSchedules.length === 0) {
        throw new Error('No active schedules found for this tag');
      }

      const nowISO = formatUTCDateDateTimeWidget(new Date());

      await Promise.all(
        activeSchedules.map((schedule) => TagsService.updateTagSchedule(schedule.id, { activeEndDate: nowISO })),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['tag-schedules'] });
      enqueueSnackbar('Tag ended successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error ending tag: ${error.message}`, { variant: 'error' });
    },
  });
}
